import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixRestaurantsMenuSettingsV1MenuOrderingSettings from './restaurants-menu-settings-v1-menu-ordering-settings-menu-ordering-settings.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface MenuOrderingSettings {
  /**
   * Menu ordering settings object ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number. Increments by 1 each time the menu ordering settings object is updated. To prevent conflicting changes, the existing revision must be specified when updating a menu ordering settings object.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the menu ordering settings object was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the menu ordering settings object was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * ID of the restaurant operation the menu belongs to. (See the Restaurants Operations API for more information.)
   * @format GUID
   */
  operationId?: string | null;
  /**
   * ID of the menu these settings apply to.
   * @readonly
   * @format GUID
   */
  menuId?: string | null;
  /** Whether online ordering is enabled for the menu. */
  onlineOrderingEnabled?: boolean | null;
  /** Menu availability settings. */
  availability?: Availability;
  /**
   * Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the menu.
   * @format GUID
   * @readonly
   */
  businessLocationId?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /** Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of menu ordering settings. */
  tags?: Tags;
}

export interface Availability extends AvailabilityAvailabilityTypeOptionsOneOf {
  /** Settings for availability on a weekly schedule. */
  weeklyScheduleOptions?: WeeklyScheduleOptions;
  /** Settings for availability within a time range. */
  timestampRangesOptions?: TimestampRangesOptions;
  /** Availability type. */
  type?: AvailabilityTypeWithLiterals;
  /**
   * Time zone in [Time Zone Database](https://www.iana.org/time-zones) format.
   * @readonly
   * @maxLength 50
   */
  timeZone?: string | null;
}

/** @oneof */
export interface AvailabilityAvailabilityTypeOptionsOneOf {
  /** Settings for availability on a weekly schedule. */
  weeklyScheduleOptions?: WeeklyScheduleOptions;
  /** Settings for availability within a time range. */
  timestampRangesOptions?: TimestampRangesOptions;
}

export enum AvailabilityType {
  /** Missing type due to an error. */
  UNSPECIFIED_AVAILABILITY_OPTION = 'UNSPECIFIED_AVAILABILITY_OPTION',
  /** Available all the time. */
  ALWAYS_AVAILABLE = 'ALWAYS_AVAILABLE',
  /** Available on specific days and times throughout the week. */
  WEEKLY_SCHEDULE = 'WEEKLY_SCHEDULE',
  /** Available during a specific time range. */
  TIMESTAMP_RANGES = 'TIMESTAMP_RANGES',
}

/** @enumType */
export type AvailabilityTypeWithLiterals =
  | AvailabilityType
  | 'UNSPECIFIED_AVAILABILITY_OPTION'
  | 'ALWAYS_AVAILABLE'
  | 'WEEKLY_SCHEDULE'
  | 'TIMESTAMP_RANGES';

export interface WeeklyScheduleOptions {
  /**
   * List of available time ranges for specific days of the week.
   * @maxSize 7
   */
  availableTimes?: DayOfWeekAvailability[];
}

export interface DayOfWeekAvailability {
  /** The day of week this availability relates to. */
  dayOfWeek?: EntitiesDayOfWeekWithLiterals;
  /** A list of time ranges during which the fulfillment should be available. */
  timeRanges?: TimeOfDayRange[];
}

export enum EntitiesDayOfWeek {
  /** Monday. */
  MON = 'MON',
  /** Tuesday. */
  TUE = 'TUE',
  /** Wednesday. */
  WED = 'WED',
  /** Thursday. */
  THU = 'THU',
  /** Friday. */
  FRI = 'FRI',
  /** Saturday. */
  SAT = 'SAT',
  /** Sunday. */
  SUN = 'SUN',
}

/** @enumType */
export type EntitiesDayOfWeekWithLiterals =
  | EntitiesDayOfWeek
  | 'MON'
  | 'TUE'
  | 'WED'
  | 'THU'
  | 'FRI'
  | 'SAT'
  | 'SUN';

export interface TimeOfDayRange {
  /** The start time in time of day representation. */
  startTime?: TimeOfDay;
  /** The end time in time of day representation. */
  endTime?: TimeOfDay;
}

export interface TimeOfDay {
  /**
   * Hours. <br />
   * Min: `0`. <br />
   * Max: `23`.
   */
  hours?: number;
  /**
   * Minutes. <br />
   * Min: `0`. <br />
   * Max: `23`.
   */
  minutes?: number;
}

export interface TimestampRangesOptions {
  /**
   * List of available time ranges.
   * @maxSize 50
   */
  ranges?: AvailableRange[];
}

export interface AvailableRange {
  /** The start time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  startTime?: Date | null;
  /** The end time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  endTime?: Date | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface CreateMenuOrderingSettingsRequest {
  /** Menu ordering settings entity details. */
  menuOrderingSettings?: MenuOrderingSettings;
}

export interface CreateMenuOrderingSettingsResponse {
  /** The created menu ordering settings entity. */
  menuOrderingSettings?: MenuOrderingSettings;
}

export interface GetMenuOrderingSettingsRequest {
  /**
   * ID of the menu ordering settings entity to retrieve.
   * @format GUID
   */
  menuOrderingSettingsId: string;
}

export interface GetMenuOrderingSettingsResponse {
  /** The requested menu ordering settings entity. */
  menuOrderingSettings?: MenuOrderingSettings;
}

export interface UpsertMenuOrderingSettingsByMenuIdRequest {
  /** Details of the menu ordering settings entity to be created or updated. */
  menuOrderingSettings: MenuOrderingSettings;
}

export interface UpsertMenuOrderingSettingsByMenuIdResponse {
  /** The created or updated menu ordering settings entity. */
  menuOrderingSettings?: MenuOrderingSettings;
}

export interface UpdateMenuOrderingSettingsRequest {
  /** Details of the menu ordering settings entity to update. */
  menuOrderingSettings: MenuOrderingSettings;
}

export interface UpdateMenuOrderingSettingsResponse {
  /** Updated menu ordering settings entity. */
  menuOrderingSettings?: MenuOrderingSettings;
}

export interface DeleteMenuOrderingSettingsRequest {
  /**
   * ID of the menu ordering settings entity to delete.
   * @format GUID
   */
  menuOrderingSettingsId?: string;
}

export interface DeleteMenuOrderingSettingsResponse {}

export interface QueryMenuOrderingSettingsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryMenuOrderingSettingsResponse {
  /** List of menu ordering settings entities. */
  menuOrderingSettings?: MenuOrderingSettings[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkUpdateMenuOrderingSettingsRequest {
  /**
   * Menu ordering settings entities to update.
   * @minSize 1
   * @maxSize 100
   */
  menusOrderingSettings: MaskedMenuOrderingSettings[];
  /** Whether to receive the entity in the response. */
  returnEntity?: boolean;
}

export interface MaskedMenuOrderingSettings {
  /** Menu ordering settings entities to update. */
  menuOrderingSettings?: MenuOrderingSettings;
  /** Explicit list of fields to update. */
  mask?: string[];
}

export interface BulkUpdateMenuOrderingSettingsResponse {
  /**
   * Results of bulk menu ordering settings entities update.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkMenuOrderingSettingsResult[];
  /** Metadata for the API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkMenuOrderingSettingsResult {
  /** Metadata for menu update. */
  menuOrderingSettingsMetadata?: ItemMetadata;
  /** Updated menu ordering settings. Only returned if `returnEntity` is set to `true`. */
  menuOrderingSettings?: MenuOrderingSettings;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface ListMenusAvailabilityStatusRequest {
  /** The time slot for which to check the availability of menus. */
  timeSlot: TimeSlot;
  /**
   * The ID of the restaurant operation whose menus will be checked. (See the Restaurants Operations API for more information.)
   * @format GUID
   */
  operationId?: string | null;
  /** Cursor paging */
  cursorPaging?: CursorPaging;
}

export interface TimeSlot {
  /** The start time of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  startTime?: Date | null;
  /** The end time of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  endTime?: Date | null;
}

export interface ListMenusAvailabilityStatusResponse {
  /**
   * The retrieved menus' availability statuses.
   * @maxSize 100
   */
  menusAvailabilityStatus?: MenuAvailabilityStatus[];
  /** The metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface MenuAvailabilityStatus {
  /**
   * Menu ID.
   * @format GUID
   */
  menuId?: string;
  /** The menu's availability status. */
  availabilityStatus?: AvailabilityStatusWithLiterals;
}

export enum AvailabilityStatus {
  /** Unknown availability status. */
  UNKNOWN_AVAILABILITY_STATUS = 'UNKNOWN_AVAILABILITY_STATUS',
  /** Available. */
  AVAILABLE = 'AVAILABLE',
  /** Unavailable. */
  UNAVAILABLE = 'UNAVAILABLE',
}

/** @enumType */
export type AvailabilityStatusWithLiterals =
  | AvailabilityStatus
  | 'UNKNOWN_AVAILABILITY_STATUS'
  | 'AVAILABLE'
  | 'UNAVAILABLE';

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  _id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** The updated menu ordering settings entity. */
  menuOrderingSettings?: MenuOrderingSettings;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface GetRestaurantsAppClonesStatusRequest {}

export interface GetRestaurantsAppClonesStatusResponse {
  /** The requested menu ordering settings entity. */
  retaurantsAppCloneStatus?: RestaurantsAppCloneStatus;
}

/** The status of the data cloning process of the Restaurants Apps. */
export interface RestaurantsAppCloneStatus {
  /**
   * Whether the cloning of the orders was completed.
   * @readonly
   */
  ordersCloningCompleted?: boolean;
  /**
   * Whether the cloning of the menus was completed.
   * @readonly
   */
  menusCloningCompleted?: boolean;
}

/** Encapsulates all details written to the Greyhound topic when a site's properties are updated. */
export interface SitePropertiesNotification {
  /** The site ID for which this update notification applies. */
  metasiteId?: string;
  /** The actual update event. */
  event?: SitePropertiesEvent;
  /**
   * A convenience set of mappings from the MetaSite ID to its constituent services.
   * @maxSize 500
   */
  translations?: Translation[];
  /** Context of the notification */
  changeContext?: ChangeContext;
}

/** The actual update event for a particular notification. */
export interface SitePropertiesEvent {
  /** Version of the site's properties represented by this update. */
  version?: number;
  /** Set of properties that were updated - corresponds to the fields in "properties". */
  fields?: string[];
  /** Updated properties. */
  properties?: Properties;
}

export interface Properties {
  /** Site categories. */
  categories?: Categories;
  /** Site locale. */
  locale?: Locale;
  /**
   * Site language.
   *
   * Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   */
  language?: string | null;
  /**
   * Site currency format used to bill customers.
   *
   * Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   */
  paymentCurrency?: string | null;
  /** Timezone in `America/New_York` format. */
  timeZone?: string | null;
  /** Email address. */
  email?: string | null;
  /** Phone number. */
  phone?: string | null;
  /** Fax number. */
  fax?: string | null;
  /** Address. */
  address?: Address;
  /** Site display name. */
  siteDisplayName?: string | null;
  /** Business name. */
  businessName?: string | null;
  /** Path to the site's logo in Wix Media (without Wix Media base URL). */
  logo?: string | null;
  /** Site description. */
  description?: string | null;
  /**
   * Business schedule. Regular and exceptional time periods when the business is open or the service is available.
   *
   * __Note:__ Not supported by Wix Bookings.
   */
  businessSchedule?: BusinessSchedule;
  /** Supported languages of a site and the primary language. */
  multilingual?: Multilingual;
  /** Cookie policy the Wix user defined for their site (before the site visitor interacts with/limits it). */
  consentPolicy?: ConsentPolicy;
  /**
   * Supported values: `FITNESS SERVICE`, `RESTAURANT`, `BLOG`, `STORE`, `EVENT`, `UNKNOWN`.
   *
   * Site business type.
   */
  businessConfig?: string | null;
  /** External site URL that uses Wix as its headless business solution. */
  externalSiteUrl?: string | null;
  /** Track clicks analytics. */
  trackClicksAnalytics?: boolean;
  /**
   * Company ID issued to the business by local authorities. Appears on invoices and price quotes.
   * @maxLength 50
   */
  companyId?: string | null;
}

export interface Categories {
  /** Primary site category. */
  primary?: string;
  /**
   * Secondary site category.
   * @maxSize 50
   */
  secondary?: string[];
  /** Business Term Id */
  businessTermId?: string | null;
}

export interface Locale {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format. */
  country?: string;
}

export interface Address {
  /** Street name. */
  street?: string;
  /** City name. */
  city?: string;
  /** Two-letter country code in an [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format. */
  country?: string;
  /** State. */
  state?: string;
  /**
   * Zip or postal code.
   * @maxLength 20
   */
  zip?: string;
  /** Extra information to be displayed in the address. */
  hint?: AddressHint;
  /** Whether this address represents a physical location. */
  isPhysical?: boolean;
  /** Google-formatted version of this address. */
  googleFormattedAddress?: string;
  /** Street number. */
  streetNumber?: string;
  /** Apartment number. */
  apartmentNumber?: string;
  /** Geographic coordinates of location. */
  coordinates?: GeoCoordinates;
}

/**
 * Extra information on displayed addresses.
 * This is used for display purposes. Used to add additional data about the address, such as "In the passage".
 * Free text. In addition, the user can state where to display the additional description - before, after, or instead of the address string.
 */
export interface AddressHint {
  /** Extra text displayed next to, or instead of, the actual address. */
  text?: string;
  /** Where the extra text should be displayed. */
  placement?: PlacementTypeWithLiterals;
}

/** Where the extra text should be displayed: before, after or instead of the actual address. */
export enum PlacementType {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  REPLACE = 'REPLACE',
}

/** @enumType */
export type PlacementTypeWithLiterals =
  | PlacementType
  | 'BEFORE'
  | 'AFTER'
  | 'REPLACE';

/** Geocoordinates for a particular address. */
export interface GeoCoordinates {
  /** Latitude of the location. Must be between -90 and 90. */
  latitude?: number;
  /** Longitude of the location. Must be between -180 and 180. */
  longitude?: number;
}

/** Business schedule. Regular and exceptional time periods when the business is open or the service is available. */
export interface BusinessSchedule {
  /**
   * Weekly recurring time periods when the business is regularly open or the service is available. Limited to 100 time periods.
   * @maxSize 100
   */
  periods?: TimePeriod[];
  /**
   * Exceptions to the business's regular hours. The business can be open or closed during the exception.
   * @maxSize 100
   */
  specialHourPeriod?: SpecialHourPeriod[];
}

/** Weekly recurring time periods when the business is regularly open or the service is available. */
export interface TimePeriod {
  /** Day of the week the period starts on. */
  openDay?: DayOfWeekWithLiterals;
  /**
   * Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   */
  openTime?: string;
  /** Day of the week the period ends on. */
  closeDay?: DayOfWeekWithLiterals;
  /**
   * Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   *
   * __Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.
   */
  closeTime?: string;
}

/** Enumerates the days of the week. */
export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/** Exception to the business's regular hours. The business can be open or closed during the exception. */
export interface SpecialHourPeriod {
  /** Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  startDate?: string;
  /** End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  endDate?: string;
  /**
   * Whether the business is closed (or the service is not available) during the exception.
   *
   * Default: `true`.
   */
  isClosed?: boolean;
  /** Additional info about the exception. For example, "We close earlier on New Year's Eve." */
  comment?: string;
}

export interface Multilingual {
  /**
   * Supported languages list.
   * @maxSize 200
   */
  supportedLanguages?: SupportedLanguage[];
  /** Whether to redirect to user language. */
  autoRedirect?: boolean;
}

export interface SupportedLanguage {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Locale. */
  locale?: Locale;
  /** Whether the supported language is the primary language for the site. */
  isPrimary?: boolean;
  /** Language icon. */
  countryCode?: string;
  /** How the language will be resolved. For internal use. */
  resolutionMethod?: ResolutionMethodWithLiterals;
  /** Whether the supported language is the primary language for site visitors. */
  isVisitorPrimary?: boolean | null;
}

export enum ResolutionMethod {
  QUERY_PARAM = 'QUERY_PARAM',
  SUBDOMAIN = 'SUBDOMAIN',
  SUBDIRECTORY = 'SUBDIRECTORY',
}

/** @enumType */
export type ResolutionMethodWithLiterals =
  | ResolutionMethod
  | 'QUERY_PARAM'
  | 'SUBDOMAIN'
  | 'SUBDIRECTORY';

export interface ConsentPolicy {
  /** Whether the site uses cookies that are essential to site operation. Always `true`. */
  essential?: boolean | null;
  /** Whether the site uses cookies that affect site performance and other functional measurements. */
  functional?: boolean | null;
  /** Whether the site uses cookies that collect analytics about how the site is used (in order to improve it). */
  analytics?: boolean | null;
  /** Whether the site uses cookies that collect information allowing better customization of the experience for a current visitor. */
  advertising?: boolean | null;
  /** CCPA compliance flag. */
  dataToThirdParty?: boolean | null;
}

/** A single mapping from the MetaSite ID to a particular service. */
export interface Translation {
  /** The service type. */
  serviceType?: string;
  /** The application definition ID; this only applies to services of type ThirdPartyApps. */
  appDefId?: string;
  /** The instance ID of the service. */
  instanceId?: string;
}

export interface ChangeContext extends ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

/** @oneof */
export interface ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

export interface PropertiesChange {}

export interface SiteCreated {
  /** Origin template site id. */
  originTemplateId?: string | null;
}

export interface SiteCloned {
  /** Origin site id. */
  originMetaSiteId?: string;
}

export interface OperationsDataCloningCompleted {}

export interface MenusDataCloningCompleted {}

export interface BulkUpdateMenuOrderingSettingsTagsRequest {
  /**
   * IDs of the menu ordering settings entities to update tags for.
   * @format GUID
   * @maxSize 100
   */
  menuOrderingSettingsIds: string[];
  /** Tags to assign to the menu ordering settings entities. */
  assignTags?: Tags;
  /** Tags to unassign from the menu ordering settings entities. */
  unassignTags?: Tags;
}

export interface BulkUpdateMenuOrderingSettingsTagsResponse {
  /**
   * Results of the bulk update.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateMenuOrderingSettingsTagsResult[];
  /** Metadata for the bulk update. */
  bulkActionMetadata?: CommonBulkActionMetadata;
}

export interface CommonItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface BulkUpdateMenuOrderingSettingsTagsResult {
  /** Metadata for the updated menu ordering settings entity. */
  itemMetadata?: CommonItemMetadata;
}

export interface CommonBulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateMenuOrderingSettingsTagsByFilterRequest {
  /** Filter that determines which menu ordering settings entities to update tags for. */
  filter: Record<string, any> | null;
  /** Tags to assign to the menu ordering settings entities. */
  assignTags?: Tags;
  /** Tags to unassign from the menu ordering settings entities. */
  unassignTags?: Tags;
}

export interface BulkUpdateMenuOrderingSettingsTagsByFilterResponse {
  /**
   * Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status.
   * @format GUID
   */
  jobId?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type BulkUpdateMenuOrderingSettingsTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateMenuOrderingSettingsTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface MenuOrderingSettingsCreatedEnvelope {
  entity: MenuOrderingSettings;
  metadata: EventMetadata;
}

/** @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.MENU_ORDERING_SETTINGS_READ
 * @webhook
 * @eventType wix.restaurants.menu_settings.v1.menu_ordering_settings_created
 * @slug created
 * @documentationMaturity preview
 */
export declare function onMenuOrderingSettingsCreated(
  handler: (event: MenuOrderingSettingsCreatedEnvelope) => void | Promise<void>
): void;

export interface MenuOrderingSettingsDeletedEnvelope {
  entity: MenuOrderingSettings;
  metadata: EventMetadata;
}

/** @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.MENU_ORDERING_SETTINGS_READ
 * @webhook
 * @eventType wix.restaurants.menu_settings.v1.menu_ordering_settings_deleted
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onMenuOrderingSettingsDeleted(
  handler: (event: MenuOrderingSettingsDeletedEnvelope) => void | Promise<void>
): void;

export interface MenuOrderingSettingsUpdatedEnvelope {
  entity: MenuOrderingSettings;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/** @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.MENU_ORDERING_SETTINGS_READ
 * @webhook
 * @eventType wix.restaurants.menu_settings.v1.menu_ordering_settings_updated
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onMenuOrderingSettingsUpdated(
  handler: (event: MenuOrderingSettingsUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves a menu ordering settings entity.
 * @param menuOrderingSettingsId - ID of the menu ordering settings entity to retrieve.
 * @public
 * @requiredField menuOrderingSettingsId
 * @permissionId RESTAURANTS.MENU_ORDERING_SETTINGS_READ
 * @applicableIdentity APP
 * @returns The requested menu ordering settings entity.
 * @fqn wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.GetMenuOrderingSettings
 */
export async function getMenuOrderingSettings(
  menuOrderingSettingsId: string
): Promise<
  NonNullablePaths<
    MenuOrderingSettings,
    | `availability.weeklyScheduleOptions.availableTimes`
    | `availability.weeklyScheduleOptions.availableTimes.${number}.dayOfWeek`
    | `availability.timestampRangesOptions.ranges`
    | `availability.type`
    | `tags.privateTags.tagIds`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    menuOrderingSettingsId: menuOrderingSettingsId,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenuSettingsV1MenuOrderingSettings.getMenuOrderingSettings(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.menuOrderingSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { menuOrderingSettingsId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['menuOrderingSettingsId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Upserts a menu ordering settings entity for a given `menuId`.
 * Try to create a menu ordering settings, in case it already exists - update the menu ordering settings
 * @param menuId - ID of the menu these settings apply to.
 * @public
 * @requiredField menuId
 * @requiredField menuOrderingSettings
 * @permissionId RESTAURANTS.MENU_ORDERING_SETTINGS_UPSERT
 * @applicableIdentity APP
 * @fqn wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.UpsertMenuOrderingSettingsByMenuId
 */
export async function upsertMenuOrderingSettingsByMenuId(
  menuId: string,
  menuOrderingSettings: UpsertMenuOrderingSettingsByMenuIdMenuOrderingSettings
): Promise<
  NonNullablePaths<
    UpsertMenuOrderingSettingsByMenuIdResponse,
    | `menuOrderingSettings.availability.weeklyScheduleOptions.availableTimes`
    | `menuOrderingSettings.availability.weeklyScheduleOptions.availableTimes.${number}.dayOfWeek`
    | `menuOrderingSettings.availability.timestampRangesOptions.ranges`
    | `menuOrderingSettings.availability.type`
    | `menuOrderingSettings.tags.privateTags.tagIds`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    menuOrderingSettings: { ...menuOrderingSettings, menuId: menuId },
  });

  const reqOpts =
    ambassadorWixRestaurantsMenuSettingsV1MenuOrderingSettings.upsertMenuOrderingSettingsByMenuId(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { menuOrderingSettings: '$[1]' },
        explicitPathsToArguments: { 'menuOrderingSettings.menuId': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['menuId', 'menuOrderingSettings']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpsertMenuOrderingSettingsByMenuIdMenuOrderingSettings {
  /**
   * Menu ordering settings object ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number. Increments by 1 each time the menu ordering settings object is updated. To prevent conflicting changes, the existing revision must be specified when updating a menu ordering settings object.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the menu ordering settings object was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the menu ordering settings object was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * ID of the restaurant operation the menu belongs to. (See the Restaurants Operations API for more information.)
   * @format GUID
   */
  operationId?: string | null;
  /** Whether online ordering is enabled for the menu. */
  onlineOrderingEnabled?: boolean | null;
  /** Menu availability settings. */
  availability?: Availability;
  /**
   * Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the menu.
   * @format GUID
   * @readonly
   */
  businessLocationId?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /** Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of menu ordering settings. */
  tags?: Tags;
}

/**
 * Updates a menu ordering settings entity.
 *
 * Each time the menu ordering settings entity is updated, `revision` increments by 1. The current `revision` must be specified when updating the menu ordering settings entity.
 * This ensures you're working with the latest entity and prevents unintended overwrites.
 * @param _id - Menu ordering settings object ID.
 * @public
 * @requiredField _id
 * @requiredField menuOrderingSettings
 * @requiredField menuOrderingSettings.revision
 * @permissionId RESTAURANTS.MENU_ORDERING_SETTINGS_UPDATE
 * @applicableIdentity APP
 * @returns Updated menu ordering settings entity.
 * @fqn wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.UpdateMenuOrderingSettings
 */
export async function updateMenuOrderingSettings(
  _id: string,
  menuOrderingSettings: NonNullablePaths<
    UpdateMenuOrderingSettings,
    `revision`,
    2
  >
): Promise<
  NonNullablePaths<
    MenuOrderingSettings,
    | `availability.weeklyScheduleOptions.availableTimes`
    | `availability.weeklyScheduleOptions.availableTimes.${number}.dayOfWeek`
    | `availability.timestampRangesOptions.ranges`
    | `availability.type`
    | `tags.privateTags.tagIds`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    menuOrderingSettings: { ...menuOrderingSettings, id: _id },
  });

  const reqOpts =
    ambassadorWixRestaurantsMenuSettingsV1MenuOrderingSettings.updateMenuOrderingSettings(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.menuOrderingSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { menuOrderingSettings: '$[1]' },
        explicitPathsToArguments: { 'menuOrderingSettings.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'menuOrderingSettings']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateMenuOrderingSettings {
  /**
   * Menu ordering settings object ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number. Increments by 1 each time the menu ordering settings object is updated. To prevent conflicting changes, the existing revision must be specified when updating a menu ordering settings object.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the menu ordering settings object was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the menu ordering settings object was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * ID of the restaurant operation the menu belongs to. (See the Restaurants Operations API for more information.)
   * @format GUID
   */
  operationId?: string | null;
  /**
   * ID of the menu these settings apply to.
   * @readonly
   * @format GUID
   */
  menuId?: string | null;
  /** Whether online ordering is enabled for the menu. */
  onlineOrderingEnabled?: boolean | null;
  /** Menu availability settings. */
  availability?: Availability;
  /**
   * Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the menu.
   * @format GUID
   * @readonly
   */
  businessLocationId?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /** Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of menu ordering settings. */
  tags?: Tags;
}

/**
 * Creates a query to retrieve a list of menu ordering settings objects.
 *
 * The `queryMenuOrderingSettings()` function builds a query to retrieve a list of menu ordering settings objects and returns an `MenuOrderingSettingsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/menu-ordering-settings/menu-ordering-settings-query-builder/find) function.
 *
 * You can refine the query by chaining `MenuOrderingSettingsQueryBuilder` functions onto the query. `MenuOrderingSettingsQueryBuilder` functions enable you to filter, sort, and control the results that `queryMenuOrderingSettings()` returns.
 *
 * `queryMenuOrderingSettings()` runs with the following `MenuOrderingSettingsQueryBuilder` defaults, which you can override:
 *
 * - [`limit(50)`](/menu-ordering-settings/menu-ordering-settings-query-builder/limit)
 * - [ascending('_id')](/menu-ordering-settings/menu-ordering-settings-query-builder/ascending)
 *
 * The following `MenuOrderingSettingsQueryBuilder` functions are supported for `queryMenuOrderingSettings()`. For a full description of the menu ordering settings object, see the object returned for the [`items`](/menu-ordering-settings/menu-ordering-settings-query-result/items) property in `MenuOrderingSettingsQueryResult`.
 * @public
 * @permissionId RESTAURANTS.MENU_ORDERING_SETTINGS_READ
 * @applicableIdentity APP
 * @fqn wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.QueryMenuOrderingSettings
 */
export function queryMenuOrderingSettings(): MenuOrderingSettingsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    MenuOrderingSettings,
    'CURSOR',
    QueryMenuOrderingSettingsRequest,
    QueryMenuOrderingSettingsResponse
  >({
    func: async (payload: QueryMenuOrderingSettingsRequest) => {
      const reqOpts =
        ambassadorWixRestaurantsMenuSettingsV1MenuOrderingSettings.queryMenuOrderingSettings(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryMenuOrderingSettingsRequest['query']) => {
      const args = [query, {}] as [
        QueryMenuOrderingSettingsRequest['query'],
        {}
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryMenuOrderingSettingsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.menuOrderingSettings,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface MenuOrderingSettingsQueryResult extends QueryCursorResult {
  items: MenuOrderingSettings[];
  query: MenuOrderingSettingsQueryBuilder;
  next: () => Promise<MenuOrderingSettingsQueryResult>;
  prev: () => Promise<MenuOrderingSettingsQueryResult>;
}

export interface MenuOrderingSettingsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'operationId'
      | 'menuId'
      | 'onlineOrderingEnabled'
      | 'availability.type'
      | 'businessLocationId',
    value: any
  ) => MenuOrderingSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'operationId'
      | 'menuId'
      | 'onlineOrderingEnabled'
      | 'availability.type'
      | 'businessLocationId',
    value: any
  ) => MenuOrderingSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'operationId'
      | 'menuId'
      | 'businessLocationId',
    value: any
  ) => MenuOrderingSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'operationId'
      | 'menuId'
      | 'businessLocationId',
    value: any
  ) => MenuOrderingSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'operationId'
      | 'menuId'
      | 'businessLocationId',
    value: any
  ) => MenuOrderingSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'operationId'
      | 'menuId'
      | 'businessLocationId',
    value: any
  ) => MenuOrderingSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'operationId' | 'menuId' | 'businessLocationId',
    value: string
  ) => MenuOrderingSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'operationId'
      | 'menuId'
      | 'onlineOrderingEnabled'
      | 'availability.type'
      | 'businessLocationId',
    value: any[]
  ) => MenuOrderingSettingsQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'operationId'
      | 'menuId'
      | 'onlineOrderingEnabled'
      | 'availability.type'
      | 'businessLocationId',
    value: any
  ) => MenuOrderingSettingsQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'operationId'
      | 'menuId'
      | 'onlineOrderingEnabled'
      | 'availability.type'
      | 'businessLocationId',
    value: boolean
  ) => MenuOrderingSettingsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'operationId'
      | 'menuId'
      | 'onlineOrderingEnabled'
      | 'availability.type'
      | 'businessLocationId'
    >
  ) => MenuOrderingSettingsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'operationId'
      | 'menuId'
      | 'onlineOrderingEnabled'
      | 'availability.type'
      | 'businessLocationId'
    >
  ) => MenuOrderingSettingsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => MenuOrderingSettingsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => MenuOrderingSettingsQueryBuilder;
  find: () => Promise<MenuOrderingSettingsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.QueryMenuOrderingSettings
 * @requiredField query
 */
export async function typedQueryMenuOrderingSettings(
  query: MenuOrderingSettingsQuery
): Promise<
  NonNullablePaths<
    QueryMenuOrderingSettingsResponse,
    `menuOrderingSettings` | `menuOrderingSettings.${number}.availability.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixRestaurantsMenuSettingsV1MenuOrderingSettings.queryMenuOrderingSettings(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface MenuOrderingSettingsQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'availability.type',
        'businessLocationId',
        'menuId',
        'onlineOrderingEnabled',
        'operationId'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  MenuOrderingSettings,
  MenuOrderingSettingsQuerySpec
>;
export type MenuOrderingSettingsQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      MenuOrderingSettings,
      MenuOrderingSettingsQuerySpec,
      MenuOrderingSettingsQuery
    >(),
  },
};

/**
 * Updates multiple menu ordering settings entities at once.
 * Each time a menu ordering settings entity is updated, `revision` increments by 1. The existing revision must be specified when updating the menu ordering settings entity. This ensures you're working with the entity's latest information, and it prevents unintended overwrites.
 * @param menusOrderingSettings - Menu ordering settings entities to update.
 * @public
 * @requiredField menusOrderingSettings
 * @requiredField menusOrderingSettings.menuOrderingSettings._id
 * @requiredField menusOrderingSettings.menuOrderingSettings.revision
 * @permissionId RESTAURANTS.MENU_ORDERING_SETTINGS_UPDATE
 * @applicableIdentity APP
 * @fqn wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.BulkUpdateMenuOrderingSettings
 */
export async function bulkUpdateMenuOrderingSettings(
  menusOrderingSettings: NonNullablePaths<
    MaskedMenuOrderingSettings,
    `menuOrderingSettings._id` | `menuOrderingSettings.revision`,
    3
  >[],
  options?: BulkUpdateMenuOrderingSettingsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateMenuOrderingSettingsResponse,
    | `results`
    | `results.${number}.menuOrderingSettingsMetadata.originalIndex`
    | `results.${number}.menuOrderingSettingsMetadata.success`
    | `results.${number}.menuOrderingSettingsMetadata.error.code`
    | `results.${number}.menuOrderingSettingsMetadata.error.description`
    | `results.${number}.menuOrderingSettings.availability.type`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    menusOrderingSettings: menusOrderingSettings,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenuSettingsV1MenuOrderingSettings.bulkUpdateMenuOrderingSettings(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          menusOrderingSettings: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['menusOrderingSettings', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateMenuOrderingSettingsOptions {
  /** Whether to receive the entity in the response. */
  returnEntity?: boolean;
}

/**
 * Retrieves a list of a menu's availability statuses for a given time slot and restaurant operation. (See the Restaurants Operations API for more information.)
 * Returns the availability status for the given time slot per menu.
 * @param timeSlot - The time slot for which to check the availability of menus.
 * @public
 * @requiredField timeSlot
 * @requiredField timeSlot.endTime
 * @requiredField timeSlot.startTime
 * @permissionId RESTAURANTS.MENU_ORDERING_SETTINGS_READ
 * @applicableIdentity APP
 * @fqn wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.ListMenusAvailabilityStatus
 */
export async function listMenusAvailabilityStatus(
  timeSlot: NonNullablePaths<TimeSlot, `endTime` | `startTime`, 2>,
  options?: ListMenusAvailabilityStatusOptions
): Promise<
  NonNullablePaths<
    ListMenusAvailabilityStatusResponse,
    | `menusAvailabilityStatus`
    | `menusAvailabilityStatus.${number}.menuId`
    | `menusAvailabilityStatus.${number}.availabilityStatus`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    timeSlot: timeSlot,
    operationId: options?.operationId,
    cursorPaging: options?.cursorPaging,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenuSettingsV1MenuOrderingSettings.listMenusAvailabilityStatus(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          timeSlot: '$[0]',
          operationId: '$[1].operationId',
          cursorPaging: '$[1].cursorPaging',
        },
        singleArgumentUnchanged: false,
      },
      ['timeSlot', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListMenusAvailabilityStatusOptions {
  /**
   * The ID of the restaurant operation whose menus will be checked. (See the Restaurants Operations API for more information.)
   * @format GUID
   */
  operationId?: string | null;
  /** Cursor paging */
  cursorPaging?: CursorPaging;
}

/**
 * Updates extended fields of a menu ordering settings entity without incrementing its revision.
 * @param _id - ID of the entity to update.
 * @param namespace - Identifier for the app whose extended fields are being updated.
 * @public
 * @requiredField _id
 * @requiredField namespace
 * @requiredField options
 * @requiredField options.namespaceData
 * @permissionId RESTAURANTS.MENU_ORDERING_SETTINGS_UPDATE
 * @applicableIdentity APP
 * @fqn wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.UpdateExtendedFields
 */
export async function updateExtendedFields(
  _id: string,
  namespace: string,
  options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
): Promise<
  NonNullablePaths<
    UpdateExtendedFieldsResponse,
    | `menuOrderingSettings.availability.weeklyScheduleOptions.availableTimes`
    | `menuOrderingSettings.availability.weeklyScheduleOptions.availableTimes.${number}.dayOfWeek`
    | `menuOrderingSettings.availability.timestampRangesOptions.ranges`
    | `menuOrderingSettings.availability.type`
    | `menuOrderingSettings.tags.privateTags.tagIds`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    namespace: namespace,
    namespaceData: options?.namespaceData,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenuSettingsV1MenuOrderingSettings.updateExtendedFields(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          namespace: '$[1]',
          namespaceData: '$[2].namespaceData',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'namespace', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateExtendedFieldsOptions {
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

/**
 * Synchronously update tags on multiple menu ordering settings entities.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 * @param menuOrderingSettingsIds - IDs of the menu ordering settings entities to update tags for.
 * @public
 * @requiredField menuOrderingSettingsIds
 * @permissionId RESTAURANTS.MENU_ORDERING_SETTINGS_UPDATE_TAGS
 * @applicableIdentity APP
 * @fqn wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.BulkUpdateMenuOrderingSettingsTags
 */
export async function bulkUpdateMenuOrderingSettingsTags(
  menuOrderingSettingsIds: string[],
  options?: BulkUpdateMenuOrderingSettingsTagsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateMenuOrderingSettingsTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkUpdateMenuOrderingSettingsTagsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    menuOrderingSettingsIds: menuOrderingSettingsIds,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenuSettingsV1MenuOrderingSettings.bulkUpdateMenuOrderingSettingsTags(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          menuOrderingSettingsIds: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['menuOrderingSettingsIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateMenuOrderingSettingsTagsOptions {
  /** Tags to assign to the menu ordering settings entities. */
  assignTags?: Tags;
  /** Tags to unassign from the menu ordering settings entities. */
  unassignTags?: Tags;
}

/**
 * Asynchronously update tags on multiple menu ordering settings entities according to the specified filter.
 * If a filter isn't specified, this method updates all menu ordering settings entities.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 * @param filter - Filter that determines which menu ordering settings entities to update tags for.
 * @public
 * @requiredField filter
 * @permissionId RESTAURANTS.MENU_ORDERING_SETTINGS_UPDATE_TAGS
 * @applicableIdentity APP
 * @fqn wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.BulkUpdateMenuOrderingSettingsTagsByFilter
 */
export async function bulkUpdateMenuOrderingSettingsTagsByFilter(
  filter: Record<string, any>,
  options?: BulkUpdateMenuOrderingSettingsTagsByFilterOptions
): Promise<
  NonNullablePaths<
    BulkUpdateMenuOrderingSettingsTagsByFilterResponse,
    `jobId`,
    2
  > & {
    __applicationErrorsType?: BulkUpdateMenuOrderingSettingsTagsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenuSettingsV1MenuOrderingSettings.bulkUpdateMenuOrderingSettingsTagsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateMenuOrderingSettingsTagsByFilterOptions {
  /** Tags to assign to the menu ordering settings entities. */
  assignTags?: Tags;
  /** Tags to unassign from the menu ordering settings entities. */
  unassignTags?: Tags;
}
